import React, { useEffect, useRef, useState } from 'react';
import Keyboard, { KeyboardReactInterface } from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import { SimpleKeyboardLayouts } from 'simple-keyboard-layouts';
import { LayoutItem, LayoutItemObj } from 'simple-keyboard-layouts/build/interfaces';
import { useLocale } from '@davi-ai/retorik-framework';
import langToLanguage from '../translations/langToLanguage.json';
import translation from '../translations/keyboard.json';
import styles from './VirtualKeyboard.module.css';

const layouts = new SimpleKeyboardLayouts();

// Simulate a React-controlled input change without react-dom/test-utils
const simulateInputChange = (target: HTMLInputElement | HTMLTextAreaElement, value: string) => {
  const proto =
    target instanceof HTMLTextAreaElement
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
  const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (nativeSetter) {
    nativeSetter.call(target, value);
    target.dispatchEvent(new Event('input', { bubbles: true }));
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

const VirtualKeyboard: React.FC = () => {
  const locale: string = useLocale();

  const [observer] = useState(
    () => new MutationObserver((m) => handleMutation(m))
  );
  const [shadowObserver] = useState(
    () => new MutationObserver((m) => handleShadowMutation(m))
  );
  const [currentLayout, setCurrentLayout] = useState<LayoutItem | LayoutItemObj | undefined>();
  const [currentLayoutName, setCurrentLayoutName] = useState<string>('default');
  const [displayKeyboard, setDisplayKeyboard] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: window.innerWidth > window.innerHeight ? window.innerWidth / 2 : window.innerWidth / 8,
    y: window.innerHeight / 2,
  });

  const divRef = useRef<HTMLDivElement | null>(null);
  const positionRef = useRef<{ x: number; y: number }>({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const isDraggingRef = useRef<boolean>(false);
  const keyboardRef = useRef<KeyboardReactInterface | null>(null);
  const keyboardTargetRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const retorikShadowRef = useRef<ShadowRoot | null>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update layout when locale changes
  useEffect(() => {
    const lang = locale.split('-')[0];
    const langMap = langToLanguage as Record<string, { name: string; nativeName: string }>;
    const language = (langMap[lang] ?? langMap['en']).name.toLowerCase();
    setCurrentLayout(layouts.get(language));
  }, [locale]);

  // Start observing document.body
  useEffect(() => {
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [observer]);

  const handleMutation = (mutations: MutationRecord[]) => {
    mutations.forEach((mutation) => {
      if (!mutation.addedNodes.length) return;
      const shadow = Array.from(mutation.addedNodes).find(
        (node) => (node as HTMLElement).id === 'retorik-shadow'
      ) as HTMLElement | undefined;
      const shadowRoot = shadow?.shadowRoot;
      if (shadowRoot) {
        retorikShadowRef.current = shadowRoot;
        shadowObserver.observe(shadowRoot, { childList: true, subtree: true });
      } else {
        processNode(mutation.addedNodes[0] as Element);
      }
    });
  };

  const handleShadowMutation = (mutations: MutationRecord[]) => {
    mutations.forEach((mutation) => {
      if (!mutation.addedNodes.length) return;
      Array.from(mutation.addedNodes).forEach((node) => processNode(node as Element));
    });
  };

  const processNode = (node: Element) => {
    const tag = node.tagName?.toLowerCase();
    if (tag === 'textarea' || tag === 'input') {
      addKeyboardTarget(node as HTMLInputElement | HTMLTextAreaElement);
    }
    if (node.children?.length) processChildren(node.children);
  };

  const processChildren = (children: HTMLCollection) => {
    Array.from(children).forEach((child) => processNode(child));
  };

  const addKeyboardTarget = (target: HTMLInputElement | HTMLTextAreaElement) => {
    target.addEventListener('focus', (e) => handleFocus(e as FocusEvent & { target: typeof target }));
    target.addEventListener('blur', handleBlur);
    const isActive =
      document.activeElement === target || retorikShadowRef.current?.activeElement === target;
    if (isActive) handleFocus({ target } as any);
  };

  const handleFocus = (e: { target: HTMLInputElement | HTMLTextAreaElement }) => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    setDisplayKeyboard(true);
    keyboardTargetRef.current = e.target;
    keyboardRef.current?.setInput(keyboardTargetRef.current?.value ?? '');
  };

  const handleBlur = () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => setDisplayKeyboard(false), 100);
  };

  const handleClick = () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    setDisplayKeyboard(true);
  };

  const handleKeyReleased = (button: string) => {
    if (button === '{shift}' || button === '{lock}') {
      toggleShift();
    } else if (button === '{enter}' && keyboardTargetRef.current) {
      keyboardTargetRef.current.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'Enter', bubbles: true })
      );
    }
  };

  const handleChange = (input: string) => {
    if (keyboardTargetRef.current && input !== keyboardTargetRef.current.value) {
      simulateInputChange(keyboardTargetRef.current, input);
    }
  };

  const toggleShift = () => {
    setCurrentLayoutName((curr) => (curr === 'default' ? 'shift' : 'default'));
  };

  const handlePointerDown = (event: React.PointerEvent) => {
    setIsDragging(true);
    isDraggingRef.current = true;
    const rect = divRef.current?.getBoundingClientRect();
    if (rect) {
      positionRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    setPosition({ x: event.clientX - positionRef.current.x, y: event.clientY - positionRef.current.y });
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    isDraggingRef.current = false;
  };

  const handleClose = (event: React.MouseEvent) => {
    keyboardTargetRef.current?.blur();
    event.preventDefault();
    event.stopPropagation();
  };

  const dragLabel =
    (translation as Record<string, { drag: string }>)[locale]?.drag ??
    (translation as Record<string, { drag: string }>)['fr-FR']?.drag ??
    'Cliquez et faites glisser';

  return (
    <div
      ref={divRef}
      className={styles.keyboard}
      style={{
        display: displayKeyboard ? 'block' : 'none',
        top: position.y,
        left: position.x,
      }}
      onClick={handleClick}
    >
      {/* Drag handle */}
      <div
        className={styles.handle}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerMove={handlePointerMove}
      >
        <span className={styles.handleLabel}>{dragLabel}</span>
        <button className={styles.closeBtn} onClick={handleClose} aria-label="Fermer le clavier">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            width={20}
            height={20}
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <Keyboard
        keyboardRef={(ref) => (keyboardRef.current = ref)}
        {...currentLayout}
        layoutName={currentLayoutName}
        onKeyReleased={handleKeyReleased}
        onChange={handleChange}
        preventMouseDownDefault
        preventMouseUpDefault
        stopMouseDownPropagation
        stopMouseUpPropagation
      />
    </div>
  );
};

export default VirtualKeyboard;
